import { createSlice } from '@reduxjs/toolkit'
import { keyBy } from '../../../helpers/common'
import { register, syncInitialState } from '../../../helpers/reducerRegistry'
import { USER_TYPE } from '../../../utils/authorization'
import { COMPANY_TYPES } from '../../../utils/common'
import { PANELS } from '../constant'
import { getDetailPanel, getKeyPanels } from '../helper'
import {
  getAlertHighlight,
  getCompanyAlerts,
  getDefaultTemplateForLayoutThunk,
  getDefaultTemplateThunk,
  getFilingStatus,
  getIndustryTopYear,
  getKeyFinancials,
  getOwnershipAndGroup,
  getOwnershipAndGroupInfo,
  getRecentNews,
  getRevenue,
  getScore,
  getSummaryHighlight,
  getSummaryOverview,
  getTemplateDetailThunk,
  getTemplatesSearchThunk,
  getTemplatesThunk,
  getTopIndustryPlayers,
} from './thunk'

const initialState = {
  highlight: {},
  overview: {},
  ownershipAndGroup: {},
  keyFinancials: {
    data: [],
    dataById: {},
  },
  score: {},
  recentNews: {
    data: [],
    dataById: {},
  },
  alertHighlight: {},
  companyAlerts: {
    data: [],
    dataById: {},
  },
  revenue: {},
  industryYear: null,
  topIndustryPlayers: {
    data: [],
    dataById: {},
  },
  filingStatus: {},

  loading: {
    highlight: false,
    overview: true,
    ownershipAndGroup: true,
    keyFinancials: true,
    score: true,
    recentNews: true,
    alertHighlight: true,
    companyAlerts: true,
    revenue: true,
    topIndustryPlayers: true,
    filingStatus: true,
    template: false,
    defaultTemplate: false,
    defaultTemplateForLayout: false,
    templateSearch: false,
  },

  templateName: '',
  templateId: null,
  isFiinGateTemplate: false,
  templates: [],
  totalRecord: 0,
  defaultTemplate: {},
  templateSearch: {
    [COMPANY_TYPES.CORPORATE]: [],
    [COMPANY_TYPES.BANK]: [],
    [COMPANY_TYPES.SECURITIES]: [],
    [COMPANY_TYPES.NON_BANK]: [],
    [COMPANY_TYPES.FUNDS]: [],
    [COMPANY_TYPES.INSURANCE]: [],
  },
  panelById: {},
  panelId: [],
  isNotHasChange: true,
  currentTemplate: { panelId: [], panelById: {} },
}

const name = 'companyAnalysis/summary'

const persistKeys = [
  'panelById',
  'panelId',
  'templateName',
  'isFiinGateTemplate',
  'templateId',
  'isNotHasChange',
]

const slice = createSlice({
  name,
  initialState: syncInitialState({ name, initialState, persistKeys }),
  reducers: {
    applySetting(state, action) {
      const { templateId, templateName, panels, isFiinGateTemplate } =
        action.payload
      state.templateId = templateId
      state.templateName = templateName
      state.panelId = panels.map((panel) => panel.id)
      state.panelById = keyBy(panels, 'id')
      state.isFiinGateTemplate = isFiinGateTemplate
    },
    changePanelId(state, action) {
      state.panelId = action.payload
      Object.keys(state.panelById).forEach((id) => {
        if (!action.payload.includes(id)) {
          delete state.panelById[id]
        }
      })
      state.isNotHasChange = false
    },
    changeFiinGateTemplateName(state, action) {
      state.templateName = action.payload
    },
    setIsNotHasChange(state, action) {
      state.isNotHasChange = action.payload
    },
  },
  extraReducers: (builder) => {
    //getSummaryHighlight
    builder.addCase(getSummaryHighlight.pending, (state) => {
      state.loading.highlight = true
    })
    builder.addCase(getSummaryHighlight.fulfilled, (state, action) => {
      state.highlight = { ...(action.payload || {}) }
      state.loading.highlight = false
    })
    builder.addCase(getSummaryHighlight.rejected, (state, action) => {
      state.loading.highlight = action.payload.loading
    })
    //getSummaryOverview
    builder.addCase(getSummaryOverview.pending, (state) => {
      state.loading.overview = true
    })
    builder.addCase(getSummaryOverview.fulfilled, (state, action) => {
      state.overview = action.payload
      state.loading.overview = false
    })
    builder.addCase(getSummaryOverview.rejected, (state, action) => {
      state.loading.overview = action.payload.loading
    })
    //getOwnershipAndGroupInfo
    builder.addCase(getOwnershipAndGroupInfo.pending, (state) => {
      state.loading.ownershipAndGroup = true
    })
    builder.addCase(getOwnershipAndGroupInfo.fulfilled, (state, action) => {
      state.ownershipAndGroup = {
        ...state.ownershipAndGroup,
        ...action.payload,
      }
      state.loading.ownershipAndGroup = false
    })
    builder.addCase(getOwnershipAndGroupInfo.rejected, (state, action) => {
      state.loading.ownershipAndGroup = action.payload.loading
    })
    //getOwnershipAndGroup
    builder.addCase(getOwnershipAndGroup.pending, (state) => {
      state.loading.ownershipAndGroup = true
    })
    builder.addCase(getOwnershipAndGroup.fulfilled, (state, action) => {
      state.ownershipAndGroup.structure = action.payload?.structure || []
      state.loading.ownershipAndGroup = false
    })
    builder.addCase(getOwnershipAndGroup.rejected, (state, action) => {
      state.loading.ownershipAndGroup = action.payload.loading
    })
    //getKeyFinancials
    builder.addCase(getKeyFinancials.pending, (state) => {
      state.loading.keyFinancials = true
    })
    builder.addCase(getKeyFinancials.fulfilled, (state, action) => {
      const response = action.payload
      if (!response || !response.length) {
        state.highlight.totalAssets = null
      }

      const data = response
        .filter(
          (item) =>
            item.ratioCode === item.parentCode ||
            (item.ratioCode && !item.parentCode),
        )
        .map((item) => {
          //Set totalAssets to highlight
          if (item.ratioCode === 'BS64') {
            state.highlight.totalAssets =
              item.values && item.values.length
                ? item.values[item.values.length - 1].value
                : null
          }

          const percents = response.find(
            (i) =>
              i.ratioCode !== i.parentCode && i.parentCode === item.ratioCode,
          )
          const percentChange =
            percents && percents.values?.length
              ? percents.values[percents.values.length - 1].value
              : null
          return {
            ...item,
            percentChange,
          }
        })
      state.keyFinancials.data = data
      state.keyFinancials.dataById = keyBy(data, 'ratioCode')
      state.loading.keyFinancials = false
    })
    builder.addCase(getKeyFinancials.rejected, (state, action) => {
      state.loading.keyFinancials = action.payload.loading
    })
    //getScore
    builder.addCase(getScore.pending, (state) => {
      state.loading.score = true
    })
    builder.addCase(getScore.fulfilled, (state, action) => {
      state.score = action.payload
      state.loading.score = false
    })
    builder.addCase(getScore.rejected, (state, action) => {
      state.loading.score = action.payload.loading
    })
    //getRecentNews
    builder.addCase(getRecentNews.pending, (state) => {
      state.loading.recentNews = true
    })
    builder.addCase(getRecentNews.fulfilled, (state, action) => {
      state.recentNews.data = action.payload
      state.recentNews.dataById = keyBy(action.payload, 'newsId')
      state.loading.recentNews = false
    })
    builder.addCase(getRecentNews.rejected, (state, action) => {
      state.loading.recentNews = action.payload.loading
    })
    //getAlertHighlight
    builder.addCase(getAlertHighlight.pending, (state) => {
      state.loading.alertHighlight = true
    })
    builder.addCase(getAlertHighlight.fulfilled, (state, { payload = {} }) => {
      state.alertHighlight = payload
      state.loading.alertHighlight = false
    })
    builder.addCase(getAlertHighlight.rejected, (state, action) => {
      state.loading.alertHighlight = action.payload.loading
    })
    //getCompanyAlerts
    builder.addCase(getCompanyAlerts.pending, (state) => {
      state.loading.companyAlerts = true
    })
    builder.addCase(getCompanyAlerts.fulfilled, (state, action) => {
      state.companyAlerts.data = action.payload
      state.companyAlerts.dataById = keyBy(action.payload, 'recordId')
      state.loading.companyAlerts = false
    })
    builder.addCase(getCompanyAlerts.rejected, (state, action) => {
      state.loading.companyAlerts = action.payload.loading
    })
    //getRevenue
    builder.addCase(getRevenue.pending, (state) => {
      state.loading.revenue = true
    })
    builder.addCase(getRevenue.fulfilled, (state, action) => {
      state.revenue = action.payload || {}
      state.highlight.revenue = action.payload?.value
      state.loading.revenue = false
    })
    builder.addCase(getRevenue.rejected, (state, action) => {
      state.loading.revenue = action.payload.loading
    })
    //getIndustryTopYear
    builder.addCase(getIndustryTopYear.fulfilled, (state, action) => {
      state.industryYear = action.payload
    })
    //getTopIndustryPlayers
    builder.addCase(getTopIndustryPlayers.pending, (state) => {
      state.loading.topIndustryPlayers = true
    })
    builder.addCase(getTopIndustryPlayers.fulfilled, (state, action) => {
      state.topIndustryPlayers.data = action.payload
      state.topIndustryPlayers.dataById = keyBy(action.payload, 'rank')
      state.loading.topIndustryPlayers = false
    })
    builder.addCase(getTopIndustryPlayers.rejected, (state, action) => {
      state.loading.topIndustryPlayers = action.payload.loading
    })
    //getFilingStatus
    builder.addCase(getFilingStatus.pending, (state) => {
      state.loading.filingStatus = true
    })
    builder.addCase(getFilingStatus.fulfilled, (state, action) => {
      state.filingStatus = action.payload
      state.loading.filingStatus = false
    })
    builder.addCase(getFilingStatus.rejected, (state, action) => {
      state.loading.filingStatus = action.payload.loading
    })
    // get template
    builder.addCase(getTemplatesThunk.pending, (state) => {
      state.loading.template = true
    })
    builder.addCase(getTemplatesThunk.fulfilled, (state, action) => {
      state.templates = action.payload.data
      state.totalRecord = action.payload.totalRecords

      state.loading.template = false
    })
    builder.addCase(getTemplatesThunk.rejected, (state, action) => {
      state.loading.template = action.payload
    })
    // get default template
    builder.addCase(getDefaultTemplateThunk.pending, (state) => {
      state.loading.defaultTemplate = true
    })
    builder.addCase(getDefaultTemplateThunk.fulfilled, (state, action) => {
      state.defaultTemplate = action.payload

      state.loading.defaultTemplate = false
    })
    builder.addCase(getDefaultTemplateThunk.rejected, (state, action) => {
      state.loading.defaultTemplate = action.payload
    })
    // get default template for layout
    builder.addCase(getDefaultTemplateForLayoutThunk.pending, (state) => {
      state.loading.defaultTemplateForLayout = true
      state.panelId = []
      state.panelById = {}
      state.templateName = ''
      state.templateId = null
    })
    builder.addCase(
      getDefaultTemplateForLayoutThunk.fulfilled,
      (state, action) => {
        const { result, companyType, userTypeId } = action.payload
        state.templateName = result.summaryTemplateName
        state.templateId = result.summaryTemplateId
        state.isFiinGateTemplate = result.isFiinGateTemplate
        if (result?.summaryTemplateData) {
          const data = JSON.parse(result.summaryTemplateData)
          state.panelId = data.map((panel) => panel.id)
          state.panelById = keyBy(data, 'id')
        } else {
          let keysPanel = getKeyPanels(companyType)

          state.panelById = {}
          state.panelId = []

          const isUserLite = userTypeId === USER_TYPE.LITE

          if (isUserLite) {
            keysPanel = keysPanel.filter((key) =>
              [PANELS.COMPANY_OVERVIEW, PANELS.KEY_FINANCIALS].includes(key),
            )
          }

          Object.values(keysPanel).forEach((key) => {
            const panel = getDetailPanel(key, isUserLite)
            state.panelById[panel.id] = panel
            state.panelId.push(panel.id)
          })
        }

        state.loading.defaultTemplateForLayout = false
      },
    )
    builder.addCase(getDefaultTemplateForLayoutThunk.rejected, (state) => {
      state.loading.defaultTemplateForLayout = false
    })
    // get template
    builder.addCase(getTemplatesSearchThunk.pending, (state) => {
      state.loading.templateSearch = true
    })
    builder.addCase(getTemplatesSearchThunk.fulfilled, (state, action) => {
      state.templateSearch = action.payload

      state.loading.templateSearch = false
    })
    builder.addCase(getTemplatesSearchThunk.rejected, (state, action) => {
      state.loading.templateSearch = action.payload
    })
    // current template
    builder.addCase(getTemplateDetailThunk.fulfilled, (state, action) => {
      const { data, companyType, userTypeId } = action.payload
      if (data?.summaryTemplateData) {
        const panels = JSON.parse(data.summaryTemplateData)
        state.currentTemplate.panelId = panels.map((panel) => panel.id)
        state.currentTemplate.panelById = keyBy(panels, 'id')
      } else {
        let keysPanel = getKeyPanels(companyType)
        state.currentTemplate.panelById = {}
        state.currentTemplate.panelId = []
        const isUserLite = userTypeId === USER_TYPE.LITE
        if (isUserLite) {
          keysPanel = keysPanel.filter((key) =>
            [PANELS.COMPANY_OVERVIEW, PANELS.KEY_FINANCIALS].includes(key),
          )
        }
        Object.values(keysPanel).forEach((key) => {
          const panel = getDetailPanel(key, isUserLite)
          state.currentTemplate.panelById[panel.id] = panel
          state.currentTemplate.panelId.push(panel.id)
        })
      }
    })
  },
})

export const selectHighlight = (state) => state[slice.name].highlight

export const selectCompanyOverview = (callback) => (state) => {
  const data = state[slice.name].overview
  return callback ? callback(data) : data
}

export const selectOwnershipAndGroup = (callback) => (state) => {
  const data = state[slice.name].ownershipAndGroup
  return callback ? callback(data) : data
}

export const selectKeyFinancials = (callback) => (state) => {
  const data = state[slice.name].keyFinancials.data
  return callback ? callback(data) : data
}
export const selectKeyFinancialById = (id, attr) => (state) => {
  const data = state[slice.name].keyFinancials.dataById
  if (!id) return data
  return attr ? data[id][attr] : data[id]
}

export const selectScore = (callback) => (state) => {
  const data = state[slice.name].score
  return callback ? callback(data) : data
}

export const selectRecentNews = (callback) => (state) => {
  const data = state[slice.name].recentNews.data
  return callback ? callback(data) : data
}
export const selectRecentNewsById = (id, attr, callback) => (state) => {
  const data = state[slice.name].recentNews.dataById[id][attr]
  return callback ? callback(data) : data
}

export const selectAlertHighlight = (callback) => (state) => {
  const data = state[slice.name].alertHighlight
  return callback ? callback(data) : data
}
export const selectCompanyAlerts = (callback) => (state) => {
  const data = state[slice.name].companyAlerts.data
  return callback ? callback(data) : data
}
export const selectCompanyAlertsById = (id, attr) => (state) => {
  const dataById = state[slice.name].companyAlerts.dataById
  if (!id) return dataById
  if (!attr) return dataById[id]
  return dataById[id][attr]
}

export const selectRevenue = (callback) => (state) => {
  const data = state[slice.name].revenue
  return callback ? callback(data) : data
}

export const selectIndustryYear = (state) => state[slice.name].industryYear

export const selectTopIndustryPlayers = (callback) => (state) => {
  const data = state[slice.name].topIndustryPlayers.data
  return callback ? callback(data) : data
}
export const selectTopIndustryPlayersById = (id, attr) => (state) => {
  const dataById = state[slice.name].topIndustryPlayers.dataById
  if (!id) return dataById
  if (!attr) return dataById[id]
  return dataById[id][attr]
}

export const selectFilingStatus = (key) => (state) => {
  const data = state[slice.name].filingStatus
  return key ? data[key] : data
}

export const selectLoading = (type) => (state) => {
  const loadingObj = state[slice.name].loading
  if (!type) return Object.values(loadingObj).some(Boolean)
  return loadingObj[type]
}

export const selectTemplates = (state) => state[slice.name].templates

export const selectTotalRecord = (state) => state[slice.name].totalRecord

export const selectTemplateName = (state) => state[slice.name].templateName

export const selectTemplateId = (state) => state[slice.name].templateId

export const selectDefaultTemplate = (state) =>
  state[slice.name].defaultTemplate

export const selectTemplateSearch = (state) => state[slice.name].templateSearch

export const selectPanelId = (state) => state[slice.name].panelId

export const selectPanelById = (state) => state[slice.name].panelById

export const selectDetailPanel = (id) => (state) =>
  state[slice.name].panelById[id]

export const selectIsNotHasChange = (state) => state[slice.name].isNotHasChange

export const selectIsFiinGateTemplate = (state) =>
  state[slice.name].isFiinGateTemplate

export const selectCurrentTemplate = (state) =>
  state[slice.name].currentTemplate

export const {
  applySetting,
  changePanelId,
  changeFiinGateTemplateName,
  setIsNotHasChange,
} = slice.actions

register(slice.name, slice.reducer, persistKeys)
